import { create } from 'zustand';
import { ScrollView } from 'react-native';
import {
  initialSnackBarState,
  SnackBarProps,
} from '@/shared/components/SnackBar';

type StoreStateProps = {
  isStylist: boolean | null;
  activeSessionName: string;
  isInSession: boolean;
  snackbarState: SnackBarProps;
  scrollRef: React.RefObject<ScrollView> | null;
  isMobileMenuVisible: boolean;
  isPostCallModalVisible: boolean;
  isAppVersionModalVisible: boolean;
  isSendbirdConnected: boolean;
  noStylistAvailable: boolean;
  completedSessionDuration: number | undefined;
  sessionChannelUrl: string | undefined;
  estimatedSessionDuration: number | undefined;
  completedConsultationId: number | undefined;
  lookbookPurchasedMessageId: string | null;
  otherUserDisconnected: boolean;
};

const defaultState: StoreStateProps = {
  isStylist: null,
  activeSessionName: '',
  isInSession: false,
  snackbarState: initialSnackBarState,
  scrollRef: null,
  isMobileMenuVisible: false,
  isPostCallModalVisible: false,
  isAppVersionModalVisible: false,
  isSendbirdConnected: false,
  noStylistAvailable: false,
  completedSessionDuration: undefined,
  sessionChannelUrl: undefined,
  estimatedSessionDuration: undefined,
  completedConsultationId: undefined,
  lookbookPurchasedMessageId: null,
  otherUserDisconnected: false,
};

type StoreActionProps = {
  setIsStylist: (isStylist: boolean) => void;
  setActiveSessionName: (sessionName: string) => void;
  setIsInSession: (isInSession: boolean) => void;
  setSnackbarState: (snackbarState: SnackBarProps) => void;
  clearSnackBar: () => void;
  setScrollRef: (scrollRef: React.RefObject<ScrollView>) => void;
  setIsMobileMenuVisible: (visible: boolean) => void;
  toggleMobileMenu: () => void;
  closeMobileMenu: () => void;
  openMobileMenu: () => void;
  setIsPostCallModalVisible: (visible: boolean) => void;
  setIsAppVersionModalVisible: (visible: boolean) => void;
  setIsSendbirdConnected: (isSendbirdConnected: boolean) => void;
  setNoStylistAvailable: (noStylistAvailable: boolean) => void;
  setCompletedSessionDuration: (sessionDuration: number | undefined) => void;
  setSessionChannelUrl: (sessionChannelUrl: string | undefined) => void;
  setEstimatedSessionDuration: (
    estimatedSessionDuration: number | undefined,
  ) => void;
  setCompletedConsultationId: (
    completedConsultationId: number | undefined,
  ) => void;
  setLookbookPurchasedMessageId: (messageId: string | null) => void;
  setOtherUserDisconnected: (disconnected: boolean) => void;
  resetStore: () => void;
};

type StoreState = StoreStateProps & StoreActionProps;

const useStore = create<StoreState>((set) => ({
  ...defaultState,

  setIsStylist: (isStylist) => set({ isStylist }),
  setActiveSessionName: (sessionName) =>
    set({ activeSessionName: sessionName }),
  setIsInSession: (isInSession) => set({ isInSession }),
  setSnackbarState: (snackbarState) => set({ snackbarState }),
  clearSnackBar: () => set({ snackbarState: initialSnackBarState }),
  setScrollRef: (scrollRef) => set({ scrollRef }),
  setIsMobileMenuVisible: (visible) => set({ isMobileMenuVisible: visible }),
  toggleMobileMenu: () =>
    set((state) => ({ isMobileMenuVisible: !state.isMobileMenuVisible })),
  closeMobileMenu: () => set({ isMobileMenuVisible: false }),
  openMobileMenu: () => set({ isMobileMenuVisible: true }),
  setIsPostCallModalVisible: (visible) =>
    set({ isPostCallModalVisible: visible }),
  setIsAppVersionModalVisible: (visible) =>
    set({ isAppVersionModalVisible: visible }),
  setIsSendbirdConnected: (isSendbirdConnected) => set({ isSendbirdConnected }),
  setNoStylistAvailable: (noStylistAvailable) => set({ noStylistAvailable }),
  setCompletedSessionDuration: (completedSessionDuration) =>
    set({ completedSessionDuration }),
  setSessionChannelUrl: (sessionChannelUrl) => set({ sessionChannelUrl }),
  setEstimatedSessionDuration: (estimatedSessionDuration) =>
    set({ estimatedSessionDuration }),
  setCompletedConsultationId: (completedConsultationId) =>
    set({ completedConsultationId }),
  setLookbookPurchasedMessageId: (messageId) =>
    set({ lookbookPurchasedMessageId: messageId }),
  setOtherUserDisconnected: (disconnected) =>
    set({ otherUserDisconnected: disconnected }),
  resetStore: () => set({ ...defaultState }),
}));

export default useStore;
